import React, { useState, useCallback, useMemo } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import DocumentTitle from 'react-document-title';
import { toastr } from 'react-redux-toastr';

import {
  Table,
  TableHeader,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import GenerateReportButton from './GenerateReportButton';
import GenerateSubmissionFileButton from './GenerateSubmissionFileButton';
import TableLayout from '../../components/TableLayout';

import useSelectedProperty from '../../hooks/useSelectedProperty';
import useManagePicTransactions from './hooks';
import useBaseTableProps from '../../hooks/useBaseTableProps';
import PicTransactionsService from '../../services/picTransactionsService';

import { PicTransaction } from './types';
import messages from './messages';

type Props = {
  organizationId: string,
  propertyId: string,
  intl: Object,
};

const ManagePicTransactions = ({ intl }: Props) => {
  const {
    id: propertyId,
    organizationId,
    name: propertyName,
  } = useSelectedProperty();
  const baseTableProps = useBaseTableProps();

  const {
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    handleColumnChange,
    allColumnsHidden,
    columnOptions,
    selectedColumns,
    isLoading,
    count,
    totalCount,
    name,
    order,
    orderBy,
    filteredHeaders,
    rows,
    onCSVButtonClick,
    isAtLeastOneRowSelected,
    selectedAffordableQualificationIds,
  } = useManagePicTransactions({
    organizationId,
    propertyId,
    propertyName,
  });

  const [isGenerating, setIsGenerating] = useState(false);
  const picTransactionsService = useMemo(
    () => new PicTransactionsService(),
    [],
  );

  const handleGenerateSubmission = useCallback(async () => {
    if (!selectedAffordableQualificationIds.length) return;
    setIsGenerating(true);
    try {
      const result = await picTransactionsService.generatePicSubmission(
        organizationId,
        propertyId,
        selectedAffordableQualificationIds,
      );
      if (result && result.error) {
        toastr.error(
          intl.formatMessage(messages.generateSubmissionFile),
          result.error,
        );
      } else {
        toastr.success(
          intl.formatMessage(messages.generateSubmissionFile),
          intl.formatMessage(messages.generateSubmissionFileSuccess),
        );
      }
    } catch (err) {
      const message =
        err?.error || err?.message || err?.networkError || String(err);
      toastr.error(
        intl.formatMessage(messages.generateSubmissionFile),
        message,
      );
    } finally {
      setIsGenerating(false);
    }
  }, [
    organizationId,
    propertyId,
    selectedAffordableQualificationIds,
    intl,
    picTransactionsService,
  ]);

  const picTransactions: Array<PicTransaction> = rows;

  return (
    <DocumentTitle
      data-testid={'manage-pic-transactions'}
      title={intl.formatMessage(messages.title)}
    >
      <>
        <TableLayout name={name} childrenElementsHeight={113 + 158}>
          {(tableHeight) => (
            <>
              <TableHeader
                title={<FormattedMessage {...messages.title} />}
                count={count}
                totalCount={totalCount}
                actions={
                  <>
                    <GenerateSubmissionFileButton
                      disabled={!isAtLeastOneRowSelected || isGenerating}
                      showToolTip={!isAtLeastOneRowSelected}
                      onGenerateClick={handleGenerateSubmission}
                      isGenerating={isGenerating}
                      intl={intl}
                    />
                    <GenerateReportButton
                      disabled={true}
                      showToolTip={!isAtLeastOneRowSelected}
                      intl={intl}
                    />
                  </>
                }
              />
              <Table
                {...baseTableProps}
                handleSortChange={handleSortChange}
                handleFilterChange={handleFilterChange}
                handleFilterTypeChange={handleFilterTypeChange}
                handleSearchSubmit={handleSearchSubmit}
                handleDateSubmit={handleDateSubmit}
                filterState={filterState}
                filterTypeState={filterTypeState}
                searchState={searchState}
                dateState={dateState}
                handleColumnChange={handleColumnChange}
                allColumnsHidden={allColumnsHidden}
                columnOptions={columnOptions}
                selectedColumns={selectedColumns}
                isLoading={isLoading}
                count={count}
                totalCount={totalCount}
                name={name}
                order={order}
                orderBy={orderBy}
                headers={filteredHeaders}
                rows={picTransactions}
                onCSVButtonClick={count > 0 ? onCSVButtonClick : undefined}
                height={tableHeight}
                intl={intl}
              />
            </>
          )}
        </TableLayout>
      </>
    </DocumentTitle>
  );
};

export default injectIntl(ManagePicTransactions);
